import type { MockPersonaData } from '../types';
import { profile } from './profile';
import { schedules } from './schedules';
import { notificationSettings } from './notificationSettings';
import { aiConfig } from './aiConfig';

// Import aspirations, goals, and lists from different life areas
import { aspirations as healthAspirations, goals as healthGoals, lists as healthLists } from './optimal_health';
import { aspirations as financialAspirations, goals as financialGoals, lists as financialLists } from './financial_independence';
import { aspirations as relationshipAspirations, goals as relationshipGoals, lists as relationshipLists } from './strong_relationships';

// Export Dan Roberts persona data with multiple aspirations
export const danRobertsPersona: MockPersonaData = {
  profile,
  aspirations: [...healthAspirations, ...financialAspirations, ...relationshipAspirations],
  goals: [...healthGoals, ...financialGoals, ...relationshipGoals],
  lists: [...healthLists, ...financialLists, ...relationshipLists],
  schedules,
  notificationSettings,
  aiConfig
};

// Helper function to convert dates for this persona
export function initializeDanRoberts(): MockPersonaData {
  // Convert dates to ensure they're proper Date objects when loaded from storage
  const personaWithDates = {
    ...danRobertsPersona,
    aspirations: danRobertsPersona.aspirations.map(a => ({
      ...a,
      deadline: a.deadline ? new Date(a.deadline) : undefined,
      createdAt: new Date(a.createdAt),
      updatedAt: new Date(a.updatedAt),
      linkedGoals: a.linkedGoals.map(g => ({
        ...g,
        completedAt: g.completedAt ? new Date(g.completedAt) : undefined,
        deadline: g.deadline ? new Date(g.deadline) : undefined
      }))
    })),
    goals: danRobertsPersona.goals.map(g => ({
      ...g,
      deadline: g.deadline ? new Date(g.deadline) : undefined,
      startDate: g.startDate ? new Date(g.startDate) : undefined,
      completedDate: g.completedDate ? new Date(g.completedDate) : undefined,
      createdAt: new Date(g.createdAt),
      updatedAt: new Date(g.updatedAt),
      lists: g.lists.map(l => ({
        ...l,
        deadline: new Date(l.deadline),
        completedAt: l.completedAt ? new Date(l.completedAt) : undefined
      }))
    })),
    lists: danRobertsPersona.lists.map(l => ({
      ...l,
      deadline: l.deadline ? new Date(l.deadline) : undefined,
      createdAt: new Date(l.createdAt),
      updatedAt: new Date(l.updatedAt),
      completedDate: l.completedDate ? new Date(l.completedDate) : undefined,
      items: l.items.map(i => ({
        ...i,
        createdAt: new Date(i.createdAt),
        updatedAt: new Date(i.updatedAt),
        dueDate: i.dueDate ? new Date(i.dueDate) : undefined
      }))
    })),
    schedules: danRobertsPersona.schedules.map(s => ({
      ...s,
      scheduledDate: s.scheduledDate ? new Date(s.scheduledDate) : undefined,
      createdAt: new Date(s.createdAt),
      updatedAt: new Date(s.updatedAt),
      recurringPattern: s.recurringPattern ? {
        ...s.recurringPattern,
        startDate: new Date(s.recurringPattern.startDate),
        endDate: s.recurringPattern.endDate ? new Date(s.recurringPattern.endDate) : undefined,
        customDates: s.recurringPattern.customDates?.map(d => new Date(d))
      } : undefined
    })),
    notificationSettings: danRobertsPersona.notificationSettings,
    aiConfig: danRobertsPersona.aiConfig
  };

  return personaWithDates;
}